import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "api/api";
import { loginReset } from "redux/slice/authentication";
import { arrayToById, objectToById } from "redux/normalize";
import { BoardOption, ClientBoardListResource, ClientBoardResource } from "api/resource/client-board";

export interface ClientBoardsState {
    loading?: boolean;
    error?: string | null;
    byId: {
        [key: number]: ClientBoardResource;
    };
    // Office/MultiOffice account flag from the GET wrapper (D13).
    officeAccount: boolean;
    options: BoardOption[];
    optionsLoading?: boolean;
};

const initialState: ClientBoardsState = {
    byId: {},
    officeAccount: false,
    options: [],
};

const fetchClientBoards = createAsyncThunk("clientBoards/fetchClientBoards", async (payload, thunkAPI) => {
    thunkAPI.dispatch(clientBoardsLoading());
    const result = api.getClientBoards();
    result
        .then((clientBoards) => thunkAPI.dispatch(clientBoardsLoaded(clientBoards)))
        .catch((error) => thunkAPI.dispatch(clientBoardsFailed(error)));
    return result;
});

const updateClientBoard = createAsyncThunk("clientBoards/updateClientBoard", async (payload: {clientBoard: ClientBoardResource}, thunkAPI) => {
    thunkAPI.dispatch(clientBoardsLoading());
    const result = api.updateClientBoard(payload.clientBoard.id, payload.clientBoard);
    result
        .then((clientBoard) => thunkAPI.dispatch(clientBoardLoaded(clientBoard)))
        .catch((error) => thunkAPI.dispatch(clientBoardsFailed(error)));
    return result;
});

// Add a new MLS profile (a new ClientBoard row) for an already-provisioned board — CF's
// "Add Profile". POSTs to the collection; the server assigns the new row id, which
// clientBoardLoaded merges into byId so the new record appears without a refetch.
const addClientBoard = createAsyncThunk("clientBoards/addClientBoard", async (payload: {clientBoard: ClientBoardResource}, thunkAPI) => {
    thunkAPI.dispatch(clientBoardsLoading());
    return api.addClientBoard(payload.clientBoard)
        .then((clientBoard) => {
            thunkAPI.dispatch(clientBoardLoaded(clientBoard));
            return clientBoard;
        })
        .catch((error) => {
            thunkAPI.dispatch(clientBoardsFailed(error));
            // Preserve the HTTP status so the page can show CF's 409-specific copy
            // ("Board already added", mls-setup.md:102); default serialization drops it.
            return thunkAPI.rejectWithValue({ status: error && error.status });
        });
});

const fetchClientBoardOptions = createAsyncThunk("clientBoards/fetchClientBoardOptions", async (_, thunkAPI) => {
    thunkAPI.dispatch(clientBoardOptionsLoading());
    const result = api.getClientBoardOptions();
    result
        .then((options) => thunkAPI.dispatch(clientBoardOptionsLoaded(options)))
        .catch((error) => thunkAPI.dispatch(clientBoardsFailed(error)));
    return result;
});

export const clientBoardsSlice = createSlice({
    name: "clientBoards",
    initialState,
    reducers: {
        clientBoardsLoading: (state) => {
            state.loading = true;
        },
        clientBoardsLoaded: (state, action: PayloadAction<ClientBoardListResource>) => {
            state.loading = false;
            state.error = null;
            state.officeAccount = action.payload.officeAccount;
            state.byId = arrayToById(action.payload.clientBoards, "id");
        },
        clientBoardLoaded: (state, action: PayloadAction<ClientBoardResource>) => {
            const byId = objectToById(action.payload, "id");
            state.loading = false;
            state.error = null;
            state.byId = {
                ...state.byId,
                ...byId,
            };
        },
        clientBoardsFailed: (state, action) => {
            state.loading = false;
            state.optionsLoading = false;
            state.error = action.payload.message;
        },
        clientBoardOptionsLoading: (state) => {
            state.optionsLoading = true;
        },
        clientBoardOptionsLoaded: (state, action: PayloadAction<BoardOption[]>) => {
            state.optionsLoading = false;
            state.error = null;
            state.options = action.payload;
        },
        default: (state) => {
            return state;
        }
    },
    extraReducers(builder) {
        builder.addCase(loginReset, (state) => {
            return {
                ...initialState,
                error: state.error,
            };
        });
    },
});

const {
    clientBoardsLoading,
    clientBoardsLoaded,
    clientBoardLoaded,
    clientBoardsFailed,
    clientBoardOptionsLoading,
    clientBoardOptionsLoaded,
} = clientBoardsSlice.actions;
export { fetchClientBoards, updateClientBoard, addClientBoard, fetchClientBoardOptions };
